import cn from 'clsx';
import { AdvantagesBlock } from 'shared/components/AdvantagesBlock';
import { ContainerInner } from 'shared/components/ContainerInner';
import { MortgageWrapperIdName, RootMortgageWrapper } from 'shared/constants/common';
import { useIsDesktop } from '../../hooks/common';
import { BankPartnersBlockContainer } from '../BankPartnersBlockContainer';
import { ControlsContainer } from '../ControlsContainer';
import { InformationalSeoBlock } from '../InformationalSeoBlock/InformationalSeoBlock';
import { ListingContainer } from '../ListingContainer';
import { ProgramsBlockContainer } from '../ProgramsBlockContainer';
import {
  FillApplicationButtonMobile,
  useFillApplicationStickyButtons,
  useMobileStickyButtons,
} from '../stickyButtons/FillApplicationButton';

import { ErrorLogComponent } from '@cian/error-log-component';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { ETestIds } from '../../constants/testIds';
import { useGetMappedOffers } from '../../hooks/useGetMappedOffers';
import { selectIsShadowMode } from '../../selectors/dictionaries/selectIsShadowMode';
import { selectCalculatorValues } from '../../selectors/landing';
import { selectIsAuthorizedModalOpen } from '../../selectors/landing/selectIsAuthorizedModalOpen';
import { ERuntimeSettings } from '../../types/runtimeSettings';
import { useApplicationContext } from '../../utils/applicationContext';
import { CreditInfoModalContainer } from '../CreditInfoModalContainer';
import { LoginWidgetMicrofrontend } from '../microfrontends/LoginWidgetMicrofrontend';
import { QuickLinks } from '../QuickLinksContainer';
import { SeoCrossLinksContainer } from '../SeoCrossLinksContainer';
import { ShortOfferTermsModalContainer } from '../ShortOfferTermsModalContainer';
import * as styles from './styles.css';

export function MainContentContainer() {
  const isDesktop = useIsDesktop();
  const { config, logger } = useApplicationContext();
  const [btnRef, setBtnRef] = useState<HTMLElement | null>(null);
  const calculatorValues = useSelector(selectCalculatorValues);
  const { mappedOffers, isFetching } = useGetMappedOffers(calculatorValues);
  const isAuthorizedModalOpen = useSelector(selectIsAuthorizedModalOpen);
  const isSeoCrossLinksEnabled = config.get<boolean>(ERuntimeSettings.isSeoCrossLinksEnabled);
  const isWebView = config.get<boolean>(ERuntimeSettings.isWebView) ?? false;
  const showCrossLinks = isSeoCrossLinksEnabled && !isWebView;
  const isShadowMode = useSelector(selectIsShadowMode);
  const mortgageLandingRedesignEnabled = config.get<boolean>(ERuntimeSettings.mortgageLandingRedesign);
  useFillApplicationStickyButtons(mappedOffers.length);
  useMobileStickyButtons(btnRef);

  const handleSetFormRef = useCallback((node: HTMLFormElement | null) => {
    setBtnRef(node);
  }, []);

  return (
    <>
      <div
        className={cn(
          styles['root'],
          showCrossLinks && styles['root--with-crosslinks'],
          isShadowMode && styles['root--shadow-mode'],
        )}
        id={RootMortgageWrapper}
      >
        <ContainerInner>
          {showCrossLinks && <QuickLinks />}
          <div id={MortgageWrapperIdName} className={styles['wrapper']} data-testid={ETestIds.RootMortgageWrapper}>
            <ControlsContainer offersCount={mappedOffers.length} offersIsFetching={isFetching} />
            <ListingContainer mappedOffersList={mappedOffers} offersIsFetching={isFetching} />
          </div>
        </ContainerInner>
        {mortgageLandingRedesignEnabled && (
          <>
            <ContainerInner>
              <AdvantagesBlock />
            </ContainerInner>
            <ProgramsBlockContainer />
            <ContainerInner>
              <BankPartnersBlockContainer />
            </ContainerInner>
          </>
        )}
        <ContainerInner>
          <InformationalSeoBlock offersLength={mappedOffers.length} />
        </ContainerInner>
        {!isDesktop && (
          <div className={styles['overlay-background']}>
            <FillApplicationButtonMobile handleOnload={handleSetFormRef} />
          </div>
        )}
      </div>
      {showCrossLinks && (
        <ErrorLogComponent logger={logger}>
          <SeoCrossLinksContainer />
        </ErrorLogComponent>
      )}
      <CreditInfoModalContainer />
      {mortgageLandingRedesignEnabled && <ShortOfferTermsModalContainer />}
      {isAuthorizedModalOpen && <LoginWidgetMicrofrontend />}
    </>
  );
}
